import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Save as you type, for one value.
 *
 * One hook instead of save logic per field, because every surface that edits
 * dossier values wants identical behaviour: show the new value at once, save a
 * beat after the typing stops, and — the part that matters — put the old value
 * back if the server refuses. Silent data loss is worse than a Save button, so
 * a failure is never swallowed: the draft reverts and `error` is set for the
 * caller to render in place.
 *
 * Deliberately NOT applied to anything that removes or reassigns a person; a
 * debounce is the wrong shape for a destructive action.
 */

export type AutosaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

/** Long enough that a normal typing burst is one request, short enough to feel
 *  immediate. Callers should not tune this — the point is consistency. */
const DEFAULT_DELAY_MS = 700;
/** How long the "Saved" tick lingers before the row goes quiet again. */
const SAVED_LINGER_MS = 1600;

export interface UseAutosaveOptions<T> {
  /** The value the server currently holds. Changes to it reset a clean draft. */
  value: T;
  /** Persist. Reject to trigger the revert. */
  onSave: (value: T) => Promise<unknown>;
  /** Skip the write when the draft is not worth saving (e.g. empty). */
  isSavable?: (value: T) => boolean;
  /** Compare server value to draft. Defaults to reference/primitive equality. */
  isEqual?: (a: T, b: T) => boolean;
  delayMs?: number;
  disabled?: boolean;
}

export interface UseAutosaveResult<T> {
  draft: T;
  /** Update the draft and (re)arm the timer. */
  set: (value: T) => void;
  /** Save now, cancelling any pending timer — for blur or Enter. */
  flush: () => void;
  /** Drop the pending edit and go back to the server value. */
  reset: () => void;
  state: AutosaveState;
  error: string | null;
}

export function useAutosave<T>({
  value,
  onSave,
  isSavable,
  isEqual,
  delayMs = DEFAULT_DELAY_MS,
  disabled = false,
}: UseAutosaveOptions<T>): UseAutosaveResult<T> {
  const [draft, setDraft] = useState<T>(value);
  const [state, setState] = useState<AutosaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  const timer = useRef<number | null>(null);
  const linger = useRef<number | null>(null);
  // Guards a late response from a superseded save overwriting a newer one.
  const saveSeq = useRef(0);
  // Held in a ref so the debounced closure always saves the latest keystroke
  // without the timer having to be torn down and rebuilt on every character.
  const pending = useRef<T>(value);
  const dirty = useRef(false);

  const same = useCallback(
    (a: T, b: T) => (isEqual ? isEqual(a, b) : a === b),
    [isEqual],
  );

  // Adopt a new server value, but never clobber an edit in flight — otherwise
  // a background refresh (the dossier reloads after every commit) would yank
  // half-typed text out from under the cursor.
  useEffect(() => {
    if (dirty.current) return;
    setDraft(value);
    pending.current = value;
  }, [value]);

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => () => {
    clearTimer();
    if (linger.current !== null) window.clearTimeout(linger.current);
  }, []);

  const run = useCallback(async () => {
    clearTimer();
    const next = pending.current;
    if (same(next, value)) {
      dirty.current = false;
      setState('idle');
      return;
    }
    if (isSavable && !isSavable(next)) {
      // Not an error — just nothing worth writing yet. Stay dirty so a later
      // keystroke that does qualify still saves.
      return;
    }
    const seq = ++saveSeq.current;
    setState('saving');
    setError(null);
    try {
      await onSave(next);
      if (seq !== saveSeq.current) return;
      // Only go clean if nothing was typed while the request was in flight.
      // Clearing `dirty` unconditionally would let the next background refresh
      // (the dossier reloads after every commit) overwrite those newer
      // keystrokes with the value the server just acknowledged.
      const superseded = !same(pending.current, next);
      if (!superseded) {
        dirty.current = false;
        setState('saved');
        if (linger.current !== null) window.clearTimeout(linger.current);
        linger.current = window.setTimeout(() => setState('idle'), SAVED_LINGER_MS);
      }
    } catch (e) {
      if (seq !== saveSeq.current) return;
      setState('error');
      setError(messageFor(e));
      // Put the old value back rather than leaving a change on screen that was
      // never stored — unless the user has already typed past the failed
      // attempt, in which case their newer text is the thing to keep.
      if (!same(pending.current, next)) return;
      dirty.current = false;
      pending.current = value;
      setDraft(value);
    }
  }, [onSave, value, same, isSavable]);

  const set = useCallback((next: T) => {
    pending.current = next;
    dirty.current = true;
    setDraft(next);
    setError(null);
    setState('dirty');
    if (disabled) return;
    clearTimer();
    timer.current = window.setTimeout(() => { void run(); }, delayMs);
  }, [run, delayMs, disabled]);

  const flush = useCallback(() => {
    if (disabled) return;
    if (!dirty.current) return;
    void run();
  }, [run, disabled]);

  const reset = useCallback(() => {
    clearTimer();
    dirty.current = false;
    pending.current = value;
    setDraft(value);
    setState('idle');
    setError(null);
  }, [value]);

  return { draft, set, flush, reset, state, error };
}

function messageFor(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't save that.";
}
